import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { NotificationsDropdown } from './NotificationsDropdown';
import { NotificationsService } from './notificationsService';
import { Notification } from './types';
import { NavigationService } from '@/services/navigationService';
import { colors } from '@/design-system/colors';
import { fetchTeamMemberLookup } from '@/lib/entity-api';

export function NotificationsButton() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());

  // Fetch user names for comment notifications
  const fetchUserNames = useCallback(async (notifications: Notification[]): Promise<Map<string, string>> => {
    const userIds = new Set<string>();
    const emailMap = new Map<string, string>();

    notifications.forEach(notification => {
      const metadata = notification.metadata || {};
      const commenterId = metadata.commenter_id;
      const fromEmail = metadata.from_email;
      
      if (commenterId) {
        userIds.add(commenterId);
      }
      if (fromEmail) {
        // Store email for fallback
        emailMap.set(commenterId || '', fromEmail);
      }
    });

    if (userIds.size === 0) {
      return new Map();
    }

    try {
      // The company's people (generic lookup, by the API); a commenter who is no longer an active member falls back to their email below
      const wanted = new Set(userIds);
      const users = (await fetchTeamMemberLookup()).filter((person) => wanted.has(person.id));

      const nameMap = new Map<string, string>();
      (users || []).forEach((user: any) => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        nameMap.set(user.id, fullName || user.email || 'Unknown User');
      });

      // Add email fallbacks for users not found
      emailMap.forEach((email, id) => {
        if (!nameMap.has(id) && email) {
          nameMap.set(id, email);
        }
      });

      setUserNames(nameMap);
      return nameMap;
    } catch (error) {
      console.error('Error fetching user names:', error);
      return new Map();
    }
  }, []);

  // Get display name for commenter
  const getCommenterName = (notification: Notification, nameMap: Map<string, string>): string => {
    const metadata = notification.metadata || {};
    const commenterId = metadata.commenter_id;
    
    if (commenterId && nameMap.has(commenterId)) {
      return nameMap.get(commenterId)!;
    }
    
    // Fallback to email if available
    if (metadata.from_email) {
      return metadata.from_email;
    }
    
    return 'Someone';
  };

  // Format notification description with user name
  const formatNotificationDescription = (notification: Notification, nameMap: Map<string, string>): string => {
    const description = notification.description || '';
    const eventName = notification.event_name?.toLowerCase() || '';
    
    // Check if it's a comment notification
    if (eventName.includes('comment') || description.toLowerCase().includes('commented')) {
      const commenterName = getCommenterName(notification, nameMap);
      // Replace "Someone" with actual name
      return description.replace(/Someone/gi, commenterName);
    }
    
    return description;
  };

  // Load notifications
  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const data = await NotificationsService.getRecentNotifications();
      // Fetch user names first and get the nameMap directly
      const nameMap = await fetchUserNames(data);
      // Format descriptions with user names using the nameMap directly
      const formattedNotifications = data.map(notification => ({
        ...notification,
        formattedDescription: formatNotificationDescription(notification, nameMap)
      }));
      setNotifications(formattedNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load unread count
  const loadUnreadCount = async () => {
    try {
      const count = await NotificationsService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  // Initial load
  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
  }, []);

  // Refresh unread count periodically (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Refresh unread count when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      loadUnreadCount(); // Also refresh count when opening dropdown
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Extract entity type and ID from notification metadata
  const getEntityInfo = (notification: Notification): { entityType: string | null; entityId: string | null } => {
    const metadata = notification.metadata || {};
    const entityType = metadata.entity_type;
    // Try different possible ID fields based on entity type
    const entityId = metadata.entity_id 
      || metadata.task_id 
      || metadata.checklist_id 
      || metadata.specification_id 
      || metadata.warranty_id 
      || metadata.change_order_id
      || metadata.material_order_id
      || metadata.purchase_order_id
      || metadata.bill_id
      || metadata.invoice_id
      || metadata.bid_id
      || metadata.proposal_id;
    
    // If no entity_type but we have an ID, try to infer from event_name
    if (!entityType && entityId) {
      const eventName = notification.event_name?.toLowerCase() || '';
      if (eventName.includes('task') || eventName.includes('todo')) {
        return { entityType: 'task', entityId };
      }
      if (eventName.includes('checklist')) {
        return { entityType: 'checklist', entityId };
      }
      if (eventName.includes('specification')) {
        return { entityType: 'specification', entityId };
      }
      if (eventName.includes('warranty')) {
        return { entityType: 'warranty', entityId };
      }
    }
    
    return { entityType, entityId };
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    NotificationsService.markAsRead(notification.id);
    
    // Update local state (preserve formattedDescription)
    setNotifications(prev => 
      prev.map(n => 
        n.id === notification.id ? { ...n, is_read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Get entity info and navigate using NavigationService
    const { entityType, entityId } = getEntityInfo(notification);
    if (entityType && entityId) {
      NavigationService.openEntity(entityType, entityId, navigate);
    } else {
      // Fallback: just navigate to notifications page if we can't determine entity
      navigate('/notifications');
    }
    
    setIsOpen(false);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const success = await NotificationsService.markAsRead(notificationId);
    if (success) {
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleViewAll = () => {
    navigate('/notifications');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        style={{ 
          backgroundColor: colors.buttonSecondary,
          color: colors.buttonTextWhite,
          border: 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonSecondaryHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonSecondary;
        }}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-50">
          <NotificationsDropdown
            notifications={notifications}
            isLoading={isLoading}
            onNotificationClick={handleNotificationClick}
            onMarkAsRead={handleMarkAsRead}
            onViewAll={handleViewAll}
          />
        </div>
      )}
    </div>
  );
}
